import { useState, useRef, useEffect } from 'react'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface AnimatedSearchInputProps {
  value: string
  onChange: (value: string) => void
}

export const AnimatedSearchInput = ({
  value,
  onChange,
}: AnimatedSearchInputProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isExpanded])

  const handleClear = () => {
    onChange('')
    setIsExpanded(false)
  }

  if (!isExpanded) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className="h-7 w-7 p-0 rounded hover:bg-gray-100"
              onClick={() => setIsExpanded(true)}
            >
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-white text-black border border-gray-200 shadow-md">
            <p>Search</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <div className="relative flex-grow animate-in slide-in-from-right-5 duration-200">
      <Input
        ref={inputRef}
        type="text"
        placeholder="Search"
        className="p-1 px-2 pl-8 pr-8 h-7 text-xs w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1.5 text-gray-400 pointer-events-none" />
      <button
        className="absolute right-2 top-1.5 hover:bg-gray-100 rounded p-0.5"
        onClick={handleClear}
      >
        <XMarkIcon className="w-3.5 h-3.5 text-gray-400" />
      </button>
    </div>
  )
}
